import { Injectable } from '@angular/core';
import { WmHttpService, ProjectService } from '@wms/services';
import { environment } from '@wms/app';
import { IVCSConfig } from '../../interface/vcs-config';
import { IChangedFile } from '@wms/app/workspace/merge-changes/interface/merge-action-change-file';

@Injectable({
  providedIn: 'root',
})
export class VcsAPIService {
  TARGET = 'VCS';
  API_META: any = {
    repoStatus: {
      url: environment.SERVICES_BASE_PATH + 'services/projects/:projectID/vcs/repostatus',
      method: "GET"
    },
    gitStatus: {
      url: environment.SERVICES_BASE_PATH + 'services/projects/:projectID/vcs/repo/status',
      method: "GET"
    },
    getChanges: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/changes/v1',
      method: 'GET',
    },
    getChangesDetails: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/changes/details',
      method: 'GET',
    },
    restoreToRemoteTrackingBranch: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/restoreToRemoteTrackingBranch',
      method: 'GET',
    },
    validateNoChanges: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/validate-no-changes',
      method: 'GET',
    },
    pushChanges: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/push',
      method: 'POST',
    },
    pullChanges: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/pull',
      method: 'POST',
    },
    revertChanges: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/revert?changesType=:changesType',
      method: 'POST',
    },
    commitLogs: {
      url:
        environment.SERVICES_BASE_PATH + 'services/projects/:projectID/vcs/log',
      method: 'GET',
    },
    singleCommitLogs: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/diff/:commitID',
      method: 'GET',
    },
    getVcsBranches: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/branches',
      method: 'POST',
    },
    addExternal: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/push/external-repo',
      method: 'POST',
    },
    getFileDiff: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/diff?filepath=:filePath&changesType=:changesType',
      method: 'GET',
    },
    getFileDiffFromCommit: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/diff/:commitId/content?filepath=:filePath',
      method: 'GET',
    },
    saveModifiedFile: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/resources/content/project/:filePath',
      method: 'POST',
    },
    getMergeConflictDetails: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/merge/details?components=:components&changeScope=:changeScope',
      method: 'GET',
    },
    discardPullChanges: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/reset',
      method: 'GET',
    },
    getDiffOfConflictedFile: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/merge/content?filepath=:filePath',
      method: 'GET',
    },
    applyMergeChanges: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/merge/applyChanges',
      method: 'POST',
    },
    finishMerge: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/merge/finish',
      method: 'POST',
    },
    resolveConflict: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/resolveconflict',
      method: 'POST',
    },
    resolveConflictParts: {
      url:
        environment.SERVICES_BASE_PATH +
        'services/projects/:projectID/vcs/resolveConflictParts',
      method: 'POST',
    },
    compareWithParent: {
      url:'/edn-services/rest/projects/:projectID/compare_with_parent',
      method: 'GET',
      spinnerInfo: {
        showSpinner: false
      }
    },
    mergeFromParent: {
      url: '/edn-services/rest/projects/:projectID/core/merge/fromParent',
      method: 'POST',
      spinnerInfo: {
        showSpinner: false
      }
    },
    mergeToParent: {
      url: '/edn-services/rest/projects/:projectID/core/merge/toParent',
      method: 'POST',
      spinnerInfo: {
        showSpinner: false
      }
    }
  };
  constructor(
    private wmHttpService: WmHttpService,
    private projectService: ProjectService
  ) {
    this.wmHttpService.registerApiMeta(this.TARGET, this.API_META);
  }

  pullChanges() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'pullChanges',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }

  repoStatus() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'repoStatus',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }

  gitStatus() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'gitStatus',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }

  loadChanges() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'getChanges',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      }
    }) as Promise<any>;
  }

  getChangesDetails() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'getChangesDetails',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      }
    }) as Promise<any>;
  }

  restoreToRemoteTrackingBranch() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'restoreToRemoteTrackingBranch',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      }
    }) as Promise<any>;
  }

  validateNoChanges() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'validateNoChanges',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      }
    }) as Promise<any>;
  }

  pushChanges(vcsParams: any) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'pushChanges',
      data: {
        "commitMessagePrefix":vcsParams.commitMessagePrefix||'NA',
        "migrationCommitMessagePrefix" : vcsParams.migrationCommitMessagePrefix||'',
        "migrationCommitEnabled":vcsParams.migrationCommitEnabled,
      },
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }

  revertChanges(revertFile: any, changesType?:string ,params?: any) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'revertChanges',
      data: revertFile,
      pathParams: {
        projectID: this.projectService.getProjectId(),
        changesType:changesType,
      },
      params
    }) as Promise<any>;
  }

  loadCommitLogs(offset: number) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'commitLogs',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
      params: { limit: 11, offset: offset },
    }) as Promise<any>;
  }

  loadSingleCommitLogs(commitId: string) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'singleCommitLogs',
      pathParams: {
        projectID: this.projectService.getProjectId(),
        commitID: commitId,
      },
    }) as Promise<any>;
  }

  getVcsBranches(vcsConfig: IVCSConfig) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'getVcsBranches',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
      data: vcsConfig,
    }) as Promise<any>;
  }

  addExternal(vcsConfig: any) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'addExternal',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
      data: vcsConfig,
    }) as Promise<any>;
  }


  saveModifiedFile(filePath: string, modifiedFile: string) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'saveModifiedFile',
      data: modifiedFile,
      pathParams: {
        projectID: this.projectService.getProjectId(),
        filePath,
      },
    }) as Promise<any>;
  }

  getFileDifferences(filePath: string,changesType:any) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'getFileDiff',
      pathParams: {
        projectID: this.projectService.getProjectId(),
        changesType:changesType,
        filePath
      }
    }) as Promise<any>;
  }

  getMergeConflictDetails(components: string, changeScope: string) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'getMergeConflictDetails',
      pathParams: {
        projectID: this.projectService.getProjectId(),
        components,
        changeScope
      },
    }) as Promise<any>;
  }

  getFileDiffFromCommit(filePath: string, commitId: string) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'getFileDiffFromCommit',
      pathParams: {
        projectID: this.projectService.getProjectId(),
        filePath,
        commitId
      }
    }) as Promise<any>;
  }

  discardPullChanges() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'discardPullChanges',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }


  getDiffOfConflictedFile(filePath: string) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'getDiffOfConflictedFile',
      pathParams: {
        projectID: this.projectService.getProjectId(),
        filePath,
      },
    }) as Promise<any>;
  }

  applyMergeChanges(payload: IChangedFile[]) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'applyMergeChanges',
      data: payload,
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }

  finishMerge() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'finishMerge',
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }

  resolveConflictedFile(payload: any) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'resolveConflict',
      data: payload,
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }

  resolveConflictParts(payload: any) {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'resolveConflictParts',
      data: payload,
      pathParams: {
        projectID: this.projectService.getProjectId(),
      },
    }) as Promise<any>;
  }

  compareWithParent() {
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'compareWithParent',
      pathParams: {
        projectID: this.projectService.getProjectEdnDetails()?.id,
      },
    }) as Promise<any>;
  }

  mergeToParent(commitMessage: string){
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'mergeToParent',
      pathParams: {
        projectID: this.projectService.getProjectEdnDetails().id,
      },
      data: commitMessage,
    }) as Promise<any>;
  }

  mergeFromParent(commitMessage: string){
    return this.wmHttpService.execute({
      target: this.TARGET,
      action: 'mergeFromParent',
      pathParams: {
        projectID: this.projectService.getProjectEdnDetails()?.id,
      },
      data: commitMessage,
    }) as Promise<any>;
  }
}
